import React, { useCallback } from 'react';
import { Edit2 } from 'lucide-react';

/**
 * The "Сделки" tab of the client card. Extracted from a 654-line component that
 * had one named function and 28 inline handlers, which made every part of it
 * untestable in isolation.
 */

interface ChatDealsTabProps {
  activeChatDeals: any[];
  onOpenNewDeal: () => void;
  onEditDealClick: (deal: any) => void;
  onUpdateDealStatus: (dealId: number, status: 'won' | 'lost') => void;
}

const STATUS_PILL: Record<string, { className: string; label: string }> = {
  won: { className: 'pill green', label: 'Выиграна' },
  lost: { className: 'pill red', label: 'Проиграна' },
};

const money = (value: unknown): string => Number(value || 0).toLocaleString('ru-RU');

const DealRow: React.FC<{
  deal: any;
  onEdit: (deal: any) => void;
  onUpdateStatus: (dealId: number, status: 'won' | 'lost') => void;
}> = ({ deal, onEdit, onUpdateStatus }) => {
  const handleEdit = useCallback(() => onEdit(deal), [deal, onEdit]);
  const handleWon = useCallback(() => onUpdateStatus(deal.id, 'won'), [deal.id, onUpdateStatus]);
  const handleLost = useCallback(() => onUpdateStatus(deal.id, 'lost'), [deal.id, onUpdateStatus]);
  const pill = STATUS_PILL[deal.status];

  return (
    <div className="chat-deal-row">
      <div className="chat-deal-head">
        <span className="chat-deal-title">{deal.title}</span>
        <div className="chat-deal-amount">
          <div>
            {deal.originalAmount && deal.originalAmount > deal.amount ? (
              <span className="chat-deal-old-amount">{money(deal.originalAmount)} ₽</span>
            ) : null}
            <span>{money(deal.amount)} ₽</span>
          </div>
          <button type="button" className="chat-deal-edit" onClick={handleEdit} title="Редактировать заявку" aria-label={`Редактировать заявку ${deal.title}`}>
            <Edit2 size={12} />
          </button>
        </div>
      </div>

      {Array.isArray(deal.items) && deal.items.length > 0 && (
        <div className="chat-deal-items">
          {deal.items.map((item: any, idx: number) => (
            <div key={`${item.productId || item.name}-${idx}`}>
              {item.name} x{item.qty} ({money((item.price || 0) * (item.qty || 1))} ₽)
            </div>
          ))}
        </div>
      )}

      <div className="chat-deal-foot">
        <span>
          {pill
            ? <span className={pill.className} style={{ fontSize: '10px', padding: '2px 6px' }}>{pill.label}</span>
            : <span className="pill amber" style={{ fontSize: '10px', padding: '2px 6px' }}>Открыта ({deal.stage || 'Новая заявка'})</span>}
        </span>
        {deal.status === 'open' && (
          <div style={{ display: 'flex', gap: '4px' }}>
            <button type="button" className="chat-deal-win" onClick={handleWon} title="Выиграна">✓</button>
            <button type="button" className="chat-deal-lose" onClick={handleLost} title="Проиграна">✗</button>
          </div>
        )}
      </div>
    </div>
  );
};

export const ChatDealsTab: React.FC<ChatDealsTabProps> = ({
  activeChatDeals,
  onOpenNewDeal,
  onEditDealClick,
  onUpdateDealStatus,
}) => (
  <div className="side-card" role="tabpanel" id="client-panel-deals" aria-labelledby="client-tab-deals">
    <div className="side-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>Заявки (CRM)</span>
      <button type="button" className="chat-deal-new" onClick={onOpenNewDeal}>+ Новая заявка</button>
    </div>
    <div className="chat-deal-list">
      {activeChatDeals.map((deal: any) => (
        <DealRow key={deal.id} deal={deal} onEdit={onEditDealClick} onUpdateStatus={onUpdateDealStatus} />
      ))}
      {activeChatDeals.length === 0 && <div className="chat-deal-empty">Заявки не созданы</div>}
    </div>
  </div>
);
